import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "./api";
import { log } from "./logger";

const AuthCtx = createContext(null);

/** Tell the service worker to purge cached API responses on login/logout so the next
 * user on this device never sees the previous user's leads/PII/masks. */
function clearApiCache() {
  try {
    if (typeof navigator !== "undefined" && navigator.serviceWorker && navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage("CLEAR_API_CACHE");
    }
  } catch (e) { log.info("[auth] SW cache purge skipped — service worker unavailable", e); }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  // Only show "loading" if we actually have a token to validate; otherwise we're done synchronously.
  const [loading, setLoading] = useState(() => typeof window !== "undefined" && !!localStorage.getItem("sohom_token"));

  useEffect(() => {
    const token = localStorage.getItem("sohom_token");
    if (!token) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/auth/me");
        if (!cancelled) setUser(r.data);
      } catch {
        if (cancelled) return;
        localStorage.removeItem("sohom_token");
        localStorage.removeItem("sohom_user");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const login = useCallback(async (email, password) => {
    clearApiCache(); // Purge any prior user's cached leads/users before writing our own.
    const r = await api.post("/auth/login", { email, password });
    localStorage.setItem("sohom_token", r.data.access_token);
    localStorage.setItem("sohom_user", JSON.stringify(r.data.user));
    setUser(r.data.user);
    return r.data.user;
  }, []);

  const logout = useCallback(() => {
    clearApiCache();
    localStorage.removeItem("sohom_token");
    localStorage.removeItem("sohom_user");
    setUser(null);
    window.location.href = "/login";
  }, []);

  const value = useMemo(
    () => ({ user, setUser, login, logout, loading }),
    [user, login, logout, loading]
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth() {
  return useContext(AuthCtx);
}
